/**
 * RenewalDecisionView — Consolidated renewal decision page for expiring/expired contracts.
 *
 * Displays contract summary, decision history, decision recording form,
 * vendor compliance status, vendor contacts, and placeholder sections for
 * site-specific data and AI redline results (to be wired in later phases).
 *
 * Requirements: 26.1, 26.2, 26.3, 26.4, 26.5, 26.6, 26.7, 26.8, 26.9, 26.10, 26.11
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractService } from '@/services/contractService';
import { renewalDecisionService, type CreateDecisionInput } from '@/services/renewalDecisionService';
import { complianceService, type ComplianceStatusSummary } from '@/services/complianceService';
import { vendorService } from '@/services/vendorService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { useAuth } from '@/contexts/AuthContext';
import type { RenewalDecision, VendorContact } from '@/types';

// ─── Constants ─────────────────────────────────────────────────────────

const DECISION_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'Renew', label: 'Renew' },
  { value: 'Renegotiate', label: 'Renegotiate' },
  { value: 'Terminate', label: 'Terminate' },
  { value: 'Undecided', label: 'Undecided' },
];

const DECISION_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  Renew: 'green',
  Renegotiate: 'blue',
  Terminate: 'red',
  Undecided: 'gray',
};

const LIFECYCLE_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  Draft: 'gray',
  'Under Review': 'blue',
  Active: 'green',
  Expiring: 'yellow',
  Expired: 'red',
  Renewed: 'blue',
  Terminated: 'red',
  Archived: 'gray',
};

const COMPLIANCE_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  valid: 'green',
  expiring: 'yellow',
  expired: 'red',
  missing: 'gray',
};

// ─── Component ─────────────────────────────────────────────────────────

export function RenewalDecisionView() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const contractId = Number(id);

  const [decisionStatus, setDecisionStatus] = useState('');
  const [decisionNotes, setDecisionNotes] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const canWrite = user?.isAdmin ||
    user?.roles.some((r) =>
      ['Admin', 'Contract Specialist', 'Facilities Admin'].includes(r),
    );

  // ── Queries ────────────────────────────────────────────────

  const contractQuery = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => contractService.getById(contractId),
    enabled: !isNaN(contractId),
  });

  const decisionsQuery = useQuery({
    queryKey: ['renewal-decisions', contractId],
    queryFn: () => renewalDecisionService.listDecisions(contractId),
    enabled: !isNaN(contractId),
  });

  const vendorId = contractQuery.data?.contract.vendorId;

  const complianceQuery = useQuery({
    queryKey: ['compliance-status', vendorId],
    queryFn: () => complianceService.getStatus(vendorId!),
    enabled: !!vendorId,
  });

  const contactsQuery = useQuery({
    queryKey: ['vendor-contacts', vendorId],
    queryFn: () => vendorService.listContacts(vendorId!),
    enabled: !!vendorId,
  });

  // ── Mutation ───────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (input: CreateDecisionInput) =>
      renewalDecisionService.createDecision(contractId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renewal-decisions', contractId] });
      setDecisionStatus('');
      setDecisionNotes('');
      setFormError('');
      setSuccessMessage('Decision recorded successfully.');
      setTimeout(() => setSuccessMessage(''), 4000);
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // ── Handlers ───────────────────────────────────────────────

  function handleSubmitDecision(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');

    if (!decisionStatus) {
      setFormError('Please select a decision status.');
      return;
    }
    if (!decisionNotes.trim()) {
      setFormError('Decision notes are required.');
      return;
    }

    createMutation.mutate({
      decisionStatus,
      decisionNotes: decisionNotes.trim(),
    });
  }

  // ── Render guards ──────────────────────────────────────────

  if (isNaN(contractId)) {
    return <ErrorMessage message="Invalid contract ID." />;
  }

  if (contractQuery.isLoading) {
    return <LoadingSpinner message="Loading renewal decision view…" />;
  }

  if (contractQuery.error || !contractQuery.data) {
    return (
      <ErrorMessage
        message={(contractQuery.error as Error)?.message ?? 'Failed to load contract.'}
        onRetry={() => contractQuery.refetch()}
      />
    );
  }

  const { contract, siteAssociations } = contractQuery.data;
  const decisions = decisionsQuery.data ?? [];
  const complianceStatus: ComplianceStatusSummary | undefined = complianceQuery.data;
  const contacts: VendorContact[] = contactsQuery.data ?? [];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
          <Link to={`/contracts/${contractId}`} style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.8rem' }}>
            ← Back to Contract
          </Link>
        </div>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
          Renewal Decision
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.875rem' }}>
          {contract.contractTitle}
        </p>
      </div>

      {/* Contract Summary */}
      <section style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>
          Contract Summary
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', fontSize: '0.875rem' }}>
          <div>
            <span style={{ color: '#64748b' }}>Vendor:</span>{' '}
            <Link to={`/vendors/${contract.vendorId}`} style={{ color: '#c8102e', textDecoration: 'none' }}>
              Vendor #{contract.vendorId}
            </Link>
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Type:</span> {contract.contractType ?? '—'}
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Status:</span>{' '}
            <StatusBadge
              label={contract.lifecycleStatus}
              variant={LIFECYCLE_STATUS_VARIANTS[contract.lifecycleStatus] ?? 'gray'}
            />
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Effective Date:</span>{' '}
            {contract.effectiveDate ? new Date(contract.effectiveDate).toLocaleDateString() : '—'}
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Expiration Date:</span>{' '}
            {contract.expirationDate ? new Date(contract.expirationDate).toLocaleDateString() : '—'}
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Contract Value:</span>{' '}
            {contract.contractValue != null ? `$${Number(contract.contractValue).toLocaleString()}` : '—'}
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Payment Terms:</span> {contract.paymentTerms ?? '—'}
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Auto-Renewal:</span> {contract.autoRenewalTerms ?? '—'}
          </div>
        </div>
        {contract.notes && (
          <div style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
            <span style={{ color: '#64748b' }}>Notes:</span>
            <p style={{ margin: '0.25rem 0 0', color: '#334155' }}>{contract.notes}</p>
          </div>
        )}
      </section>

      {/* Decision Recording Form */}
      {canWrite && (
        <section style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>
            Record Decision
          </h2>
          <form onSubmit={handleSubmitDecision}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label htmlFor="decision-status" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>
                Decision Status
              </label>
              <select
                id="decision-status"
                value={decisionStatus}
                onChange={(e) => setDecisionStatus(e.target.value)}
                style={{
                  display: 'block',
                  width: '100%',
                  maxWidth: '300px',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem',
                }}
              >
                <option value="">Select decision…</option>
                {DECISION_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label htmlFor="decision-notes" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>
                Decision Notes <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                id="decision-notes"
                value={decisionNotes}
                onChange={(e) => setDecisionNotes(e.target.value)}
                placeholder="Enter rationale for this decision…"
                rows={4}
                style={{
                  display: 'block',
                  width: '100%',
                  maxWidth: '600px',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                }}
              />
            </div>

            {formError && (
              <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
                {formError}
              </p>
            )}

            {successMessage && (
              <p style={{ color: '#166534', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
                {successMessage}
              </p>
            )}

            <button
              type="submit"
              disabled={createMutation.isPending}
              style={{
                padding: '0.4rem 1rem',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: '#c8102e',
                color: '#fff',
                cursor: createMutation.isPending ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              {createMutation.isPending ? 'Submitting…' : 'Submit Decision'}
            </button>
          </form>
        </section>
      )}

      {/* Decision History */}
      <section style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>
          Decision History
        </h2>
        {decisionsQuery.isLoading ? (
          <LoadingSpinner message="Loading decisions…" />
        ) : decisions.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No decisions recorded yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {decisions.map((decision: RenewalDecision) => (
              <div
                key={decision.decisionId}
                style={{
                  padding: '0.75rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: '#f8fafc',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                  <StatusBadge
                    label={decision.decisionStatus}
                    variant={DECISION_STATUS_VARIANTS[decision.decisionStatus] ?? 'gray'}
                  />
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    by {decision.decidedBy} on {new Date(decision.decisionDate).toLocaleDateString()}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#334155' }}>
                  {decision.decisionNotes}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Vendor Compliance Status */}
      <section style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>
          Vendor Compliance Status
        </h2>
        {complianceQuery.isLoading ? (
          <LoadingSpinner message="Loading compliance status…" />
        ) : complianceStatus ? (
          <div>
            {complianceStatus.documents.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No compliance documents tracked.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {complianceStatus.documents.map((doc) => (
                  <div
                    key={doc.documentType}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.4rem 0.75rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                    }}
                  >
                    <span>{doc.documentType}</span>
                    <StatusBadge
                      label={doc.status}
                      variant={COMPLIANCE_STATUS_VARIANTS[doc.status] ?? 'gray'}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Unable to load compliance status.</p>
        )}
      </section>

      {/* Vendor Contacts */}
      <section style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>
          Vendor Contacts
        </h2>
        {contactsQuery.isLoading ? (
          <LoadingSpinner message="Loading contacts…" />
        ) : contacts.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No vendor contacts on file.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {contacts.map((contact: VendorContact) => (
              <div
                key={contact.contactId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                }}
              >
                <div>
                  <span style={{ fontWeight: 500 }}>{contact.contactName}</span>
                  {contact.isPrimary && (
                    <StatusBadge label="Primary" variant="green" />
                  )}
                  <span style={{ color: '#64748b', marginLeft: '0.5rem' }}>
                    {contact.contactRole}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '1rem', color: '#64748b', fontSize: '0.8rem' }}>
                  {contact.email && <span>{contact.email}</span>}
                  {contact.phone && <span>{contact.phone}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Site Associations */}
      <section style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.75rem' }}>
          Associated Sites
        </h2>
        {siteAssociations.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No sites associated with this contract.</p>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {siteAssociations.map((sa) => (
              <Link
                key={sa.siteNumber}
                to={`/sites/${sa.siteNumber}`}
                style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  color: '#c8102e',
                  textDecoration: 'none',
                  fontSize: '0.8rem',
                }}
              >
                Site {sa.siteNumber}
              </Link>
            ))}
          </div>
        )}
        <p style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '0.5rem', marginBottom: 0 }}>
          Branch ranking and circuit details will be available in a future phase.
        </p>
      </section>

      {/* Placeholder: AI Redline Results */}
      <section style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.5rem', color: '#94a3b8' }}>
          AI Redline Results
        </h2>
        <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
          AI contract review results will be displayed here once the AI Review Engine is integrated.
        </p>
      </section>

      {/* Placeholder: Vendor Spend History */}
      <section style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.5rem', color: '#94a3b8' }}>
          Vendor Spend History
        </h2>
        <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
          Vendor spend history from financial reports will be displayed here once the reporting module is integrated.
        </p>
      </section>

      {/* Placeholder: Onboarding Checklist Status */}
      <section style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, marginTop: 0, marginBottom: '0.5rem', color: '#94a3b8' }}>
          Onboarding Checklist
        </h2>
        <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
          Onboarding checklist status will be displayed here once the onboarding module is wired into this view.
        </p>
      </section>
    </div>
  );
}
